import axios, {
  AxiosRequestTransformer,
  AxiosResponse,
  AxiosRequestConfig,
} from 'axios';
import { message } from 'antd';
import constants from 'src/constants/index';
import { BusinessError } from 'src/utils/business-error';
import { h } from 'src/utils/h';
import i18n from 'i18next';
// import { openConfirmModal } from './helper';
import { getItem, getToken, setToken, setItem, setItemToSession, clearAllLS } from './localStorage';
import { history } from './history';
import { authService } from './oauth/AuthService';

// const dateTransformer: AxiosRequestTransformer = data => {
//   if (data instanceof Date) {
//     // do your specific formatting here
//     console.log(data)
//     console.log(data.toLocaleString())
//     return data.toLocaleString();
//   } else if (Array.isArray(data)) {
//     return data.map(val => dateTransformer(val));
//   } else if (typeof data === 'object' && data !== null) {
//     return Object.fromEntries(
//       Object.entries(data).map(([key, val]) => [
//         key,
//         dateTransformer(val),
//       ]),
//     );
//   } else return data;
// };

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

const service = axios.create({
  baseURL: constants.BASE_SERVICE_URL, // url = base url + request url
  timeout: 180000,
  withCredentials: true,
  cancelToken: source.token,
  // transformRequest: axios.defaults.transformRequest
  //   ? [dateTransformer].concat(
  //       axios.defaults.transformRequest,
  //     )
  //   : dateTransformer,
  // withCredentials: true // send cookies when cross-domain requests
});

//locPh cmt: không auto refresh, tự động logout sau khi hết phiên đăng nhập
// let isRefreshing = false;
// let failedQueue: Array<{
//   resolve: (value?: any) => void;
//   reject: (reason?: any) => void;
// }> = [];
// let retryCount = 0;
// const MAX_RETRY_COUNT = 2;
//
// const processQueue = (error: any, token: string | null = null) => {
//   failedQueue.forEach(prom => {
//     if (error) {
//       prom.reject(error);
//     } else {
//       prom.resolve(token);
//     }
//   });
//   failedQueue = [];
// };


// Endpoint public auth — không gắn Bearer token.
// Token cũ/revoked sau logout sẽ khiến BearerTokenAuthenticationFilter trả 401
// trước khi tới controller (vd. /api/auth/ssoLogin).
const PUBLIC_AUTH_URL_SUFFIXES = [
  '/api/auth/ssoLogin',
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgotPassword',
  '/api/auth/refresh',
  '/api/auth/checkSession',
  '/api/auth/external/dmdc/authorize-url',
  '/api/auth/external/dmdc/callback',
  '/oauth/token',
];

const isPublicAuthRequest = (url?: string): boolean => {
  if (!url) return false;
  return PUBLIC_AUTH_URL_SUFFIXES.some(suffix => url.includes(suffix));
};

// Request interceptors
service.interceptors.request.use(
  async config => {
    if (isPublicAuthRequest(config.url)) {
      if (config.headers) {
        delete config.headers['Authorization'];
        delete config.headers['authorization'];
      }
      return config;
    }

    // Sử dụng OAuth access token thay vì token cũ
    const tokens = authService.getTokens();
    const accessToken = tokens?.access_token;

    if (accessToken) {
      if (config.headers) {
        config.headers['Authorization'] = 'Bearer ' + accessToken;
        config.headers['X-Frame-Options'] = 'DENY';
      }
    }
    return config;
  },
  error => {
    Promise.reject(error);
  },
);

// let cancel: any = null;
// const CancelToken = axios.CancelToken;
// axios.defaults.cancelToken = new CancelToken((c) => {
//   cancel = c;
// });

// Response interceptors
service.interceptors.response.use(
  response => {
    const res = response.data;
    const responseStatus = response.status;
    if (
      responseStatus !== constants.AXIOS.HTTP_STATUS.SUCCESS &&
      responseStatus !== 201 &&
      responseStatus !== 204
    ) {
      // message.error(
      //   res.message || 'Error',
      //   constants.MESSAGE_DURATION,
      // );
      return Promise.reject(
        new Error(res.message || 'Error'),
      );
    } else {
      return response;
    }
  },
  async error => {
    if (error.response) {
      const pathName = constants.BASE_URL
          ? history.location.pathname.substring(
              constants.BASE_URL.length,
          )
          : history.location.pathname;
      const error_ = error.response as AxiosResponse;
      if (
        error_.status ===
          constants.AXIOS.HTTP_STATUS.BAD_REQUEST &&
        error_.data &&
        error_.data.message === 'businessError'
      ) {
        const businessErrors: BusinessError[] = JSON.parse(
          error_.data.errors,
        );
        if (businessErrors && businessErrors.length) {
          h.each(businessErrors, businessError => {
            message.error(
              i18n
                .t(`error.${businessError.errorCode}`)
                .toString(),
              constants.MESSAGE_DURATION,
            );
          });
          return Promise.reject(error);
        }
      } else if (
        error_.status ===
          constants.AXIOS.HTTP_STATUS.BAD_REQUEST
      ) {
        const isValidationError =
          error_.data?.errorCode === 'VALIDATION_ERROR' ||
          (error_.data?.data && typeof error_.data.data === 'object' && Object.keys(error_.data.data).length > 0);

        if (!isValidationError) {
          const errorMessage =
            error_.data?.message ||
            error_.data?.error ||
            'Yêu cầu không hợp lệ. Vui lòng kiểm tra lại thông tin.';
          message.error(errorMessage, constants.MESSAGE_DURATION);
        }
        return Promise.reject(error);
      } else if (
        error_.status >= 500
      ) {
        const errorMessage =
          error_.data?.message ||
          error_.data?.error ||
          'Lỗi hệ thống. Vui lòng thử lại sau.';
        message.error(errorMessage, constants.MESSAGE_DURATION);
        return Promise.reject(error);
      } else if (
        error_.status ===
          constants.AXIOS.HTTP_STATUS.UNAUTHORIZED &&
        !error_.config.url?.endsWith('/oauth/token') &&
        !error_.config.url?.endsWith('/api/auth/ssoLogin') &&
        !error_.config.url?.endsWith('/api/auth/ssoChangePassword') &&
        !error_.config.url?.includes('/api/auth/external/dmdc/') &&
        pathName !== '/login' &&
        pathName !== '/loginCallback' &&
        pathName !== '/callback' &&
        pathName !== constants.FORCE_CHANGE_PASSWORD_PATH
      ) {
        // Thử refresh OAuth token trước khi redirect
        const originalRequest = error_.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Tránh retry nhiều lần
        if (originalRequest._retry) {
          source.cancel();
          authService.clearTokens();
          sessionStorage.clear();
          clearAllLS();
          authService.flagSessionExpiredNotify();
          window.location.href = `${constants.BASE_URL}/login`;
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          // Thử refresh token
          const newTokens = await authService.refreshAccessToken();
          if (newTokens && originalRequest.headers) {
            // Retry request với token mới
            originalRequest.headers['Authorization'] = 'Bearer ' + newTokens.access_token;
            return service(originalRequest);
          } else {
            // Refresh thất bại, redirect đến login
            source.cancel();
            authService.clearTokens();
            sessionStorage.clear();
            clearAllLS();
            authService.flagSessionExpiredNotify();
            window.location.href = `${constants.BASE_URL}/login`;
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Refresh thất bại, redirect đến login
          source.cancel();
          authService.clearTokens();
          sessionStorage.clear();
          clearAllLS();
          authService.flagSessionExpiredNotify();
          window.location.href = `${constants.BASE_URL}/login`;
          return Promise.reject(error);
        }



    //locPh cmt: không auto refresh token, tự động logout sau khi hết phiên đăng nhập
        // const originalRequest = error_.config as AxiosRequestConfig & {
        //   _retry?: boolean;
        // };
        //
        // const refreshToken = getItem('mbf_sso_refresh_token');
        // if (!refreshToken) {
        //   source.cancel();
        //   sessionStorage.clear();
        //   clearAllLS();
        //   window.location.href = `${constants.BASE_URL}/login`;
        //   return Promise.reject(error);
        // }
        //
        // // Kiểm tra số lần retry để tránh vòng lặp vô hạn
        // if (retryCount >= MAX_RETRY_COUNT) {
        //   retryCount = 0;
        //   source.cancel();
        //   sessionStorage.clear();
        //   clearAllLS();
        //   window.location.href = `${constants.BASE_URL}/login`;
        //   return Promise.reject(error);
        // }
        //
        // if (isRefreshing) {
        //   return new Promise((resolve, reject) => {
        //     failedQueue.push({ resolve, reject });
        //   })
        //     .then(token => {
        //       if (originalRequest.headers) {
        //         originalRequest.headers['Authorization'] = 'Bearer ' + token;
        //       }
        //       return service(originalRequest);
        //     })
        //     .catch(err => {
        //       return Promise.reject(err);
        //     });
        // }
        //
        // originalRequest._retry = true;
        // isRefreshing = true;
        // retryCount++;
        //
        // return axios
        //   .post(
        //     `${constants.BASE_SERVICE_URL}api/auth/refresh`,
        //     { refreshToken },
        //     {
        //       headers: {
        //         'Content-Type': 'application/json',
        //       },
        //     },
        //   )
        //   .then(res => {
        //     const newAccessToken = res.data?.data?.accessToken;
        //     const newRefreshToken = res.data?.data?.refreshToken;
        //
        //     if (newAccessToken) {
        //       // Check if old token was in localStorage or sessionStorage
        //       const oldRefreshToken = localStorage.getItem('mbf_sso_refresh_token') || sessionStorage.getItem('mbf_sso_refresh_token');
        //       const isRememberMe = !!localStorage.getItem('mbf_sso_refresh_token');
        //
        //       setToken(newAccessToken, isRememberMe);
        //       if (newRefreshToken) {
        //         if (isRememberMe) {
        //           setItem('mbf_sso_refresh_token', newRefreshToken);
        //         } else {
        //           setItemToSession('mbf_sso_refresh_token', newRefreshToken);
        //         }
        //       }
        //
        //       if (originalRequest.headers) {
        //         originalRequest.headers['Authorization'] =
        //           'Bearer ' + newAccessToken;
        //       }
        //
        //       processQueue(null, newAccessToken);
        //       isRefreshing = false;
        //       retryCount = 0; // Reset retry count khi refresh thành công
        //
        //       return service(originalRequest);
        //     } else {
        //       throw new Error('Không nhận được token mới');
        //     }
        //   })
        //   .catch(refreshError => {
        //     processQueue(refreshError, null);
        //     isRefreshing = false;
        //     retryCount = 0; // Reset retry count khi refresh thất bại
        //     source.cancel();
        //     sessionStorage.clear();
        //     clearAllLS();
        //     window.location.href = `${constants.BASE_URL}/login`;
        //     return Promise.reject(refreshError);
        //   });



      } else if (
        error_.status === constants.AXIOS.HTTP_STATUS.FORBIDDEN
      ) {
        //   history.push(constants.BASE_URL + '/forbidden');
        const errorMessage =
          error_.data?.message ||
          error_.data?.error ||
          'Bạn không có quyền thực hiện chức năng này.';
        message.error(errorMessage, constants.MESSAGE_DURATION);
        return Promise.reject(error);
      }
    }
    if (error.message && error.config?.url !== '/api/auth/login') {
      message.error(
        error.message || 'Đã xảy ra lỗi. Vui lòng thử lại.',
        constants.MESSAGE_DURATION,
      );
    }
    return Promise.reject(error);
  },
);

export default service;
